import React, { useState, useEffect } from 'react';
import { useRoleContext } from '../context/RoleContext';
import { getCurrentUser, subscribeToAuthState, type User } from '../services/graphql/authService';
import { getAllBookings, getBookingsByServiceProviderId, updateBookingStatus, type BookingRecord } from '../services/graphql/bookingService';
import ProgressDialog from './ProgressDialog';
import { BookingsListLayout } from './BookingsListLayout/BookingsListLayout';
import { Layout } from './Layout';
import { useServiceKey } from '../hooks/useServiceKey';

/**
 * BaseBookingsPage - shared bookings management page for admins.
 *
 * - SuperAdmin: sees ALL bookings across all service providers
 * - ServiceProviderAdmin: sees only bookings for their assigned service provider
 */
const BaseBookingsPage: React.FC = () => {
  const { userProfile, loading: roleLoading } = useRoleContext();
  const { serviceConfig } = useServiceKey();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingBookings, setIsLoadingBookings] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isSuperAdmin = userProfile?.role === 'superadmin';

  // Get current user from GraphQL auth
  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        setCurrentUser(user);
      })
      .catch(() => {
        setCurrentUser(null);
      });

    const unsubscribe = subscribeToAuthState((user) => {
      setCurrentUser(user);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const fetchBookings = async () => {
    setIsLoadingBookings(true);
    setError(null);
    try {
      let bookingRecords: BookingRecord[] = [];
      let fetchError: Error | null = null;

      if (isSuperAdmin) {
        // SuperAdmin sees all bookings
        const result = await getAllBookings();
        bookingRecords = result.data;
        fetchError = result.error;
      } else if (userProfile?.serviceprovider_id) {
        // ServiceProviderAdmin sees only their provider's bookings
        const result = await getBookingsByServiceProviderId(userProfile.serviceprovider_id);
        bookingRecords = result.data || [];
        fetchError = result.error;
      }

      if (fetchError) {
        setError('Failed to fetch bookings.');
        console.error('Error fetching bookings:', fetchError);
        return;
      }

      setBookings(bookingRecords);
    } catch (err: any) {
      setError('Failed to fetch bookings.');
      console.error('Error fetching bookings:', err);
    } finally {
      setIsLoadingBookings(false);
    }
  };

  // Fetch when profile loads or changes
  useEffect(() => {
    if (userProfile) {
      fetchBookings();
    }
  }, [userProfile?.role, userProfile?.serviceprovider_id]);

  const handleConfirmBooking = async (bookingId: string) => {
    setSuccessMessage(null);
    setError(null);
    try {
      const { error: updateError } = await updateBookingStatus(bookingId, 'confirmed');
      if (updateError) {
        setError(`Failed to confirm booking ${bookingId}: ${updateError.message}`);
        console.error('Error confirming booking:', updateError);
        return;
      }
      setSuccessMessage(`Booking ${bookingId} confirmed successfully.`);
      fetchBookings();
    } catch (err: any) {
      setError(`Failed to confirm booking ${bookingId}: ${err.message}`);
      console.error('Error confirming booking:', err);
    }
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleCancelBooking = async (bookingId: string, reason: string) => {
    setSuccessMessage(null);
    setError(null);
    try {
      const { error: updateError } = await updateBookingStatus(bookingId, 'cancelled', reason);
      if (updateError) {
        setError(`Failed to cancel booking ${bookingId}: ${updateError.message}`);
        console.error('Error cancelling booking:', updateError);
        return;
      }
      setSuccessMessage(`Booking ${bookingId} cancelled successfully.`);
      fetchBookings();
    } catch (err: any) {
      setError(`Failed to cancel booking ${bookingId}: ${err.message}`);
      console.error('Error cancelling booking:', err);
    }
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  if (roleLoading) {
    return (
      <Layout currentUser={currentUser} serviceConfig={serviceConfig}>
        <ProgressDialog isOpen={true} message="Loading user data..." subMessage="Please wait while we load your profile." />
      </Layout>
    );
  }

  // ServiceProviderAdmin must have a service provider assigned
  if (!isSuperAdmin && !userProfile?.serviceprovider_id) {
    return (
      <Layout currentUser={currentUser} serviceConfig={serviceConfig}>
        <div className="container mx-auto">
          <div className="p-8 bg-background text-foreground">
            <div className="p-4 bg-destructive/10 text-destructive rounded-md">
              <h2 className="text-xl font-semibold mb-2">No Service Provider Associated</h2>
              <p>Your account is not associated with any service provider. Please contact an administrator.</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (isLoadingBookings) {
    return (
      <Layout currentUser={currentUser} serviceConfig={serviceConfig}>
        <ProgressDialog isOpen={isLoadingBookings} message="Loading bookings..." subMessage="Please wait while we fetch your data." />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout currentUser={currentUser} serviceConfig={serviceConfig}>
        <div className="container mx-auto">
          <div className="p-8 bg-background text-foreground">
            <div className="p-4 bg-destructive/10 text-destructive rounded-md">
              <h2 className="text-xl font-semibold mb-2">Error Loading Bookings</h2>
              <p>{error}</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const title = isSuperAdmin ? 'All Bookings' : 'Service Provider Bookings';
  const emptyMessage = isSuperAdmin
    ? 'No bookings found.'
    : 'No bookings found for this service provider.';

  return (
    <Layout currentUser={currentUser} serviceConfig={serviceConfig}>
      <div className="container mx-auto">
        <div className="p-8 bg-background text-foreground">
          <BookingsListLayout
            bookings={bookings}
            loading={isLoadingBookings}
            error={error}
            successMessage={successMessage}
            currentUser={currentUser}
            mode="serviceprovider"
            title={title}
            emptyMessage={emptyMessage}
            loadingMessage="Loading bookings..."
            onConfirm={handleConfirmBooking}
            onCancel={handleCancelBooking}
            showRefreshButton={true}
            onRefresh={fetchBookings}
            className="max-w-6xl mx-auto"
          />
        </div>
      </div>
    </Layout>
  );
};

export default BaseBookingsPage;
